import {useState, useEffect} from 'react';
import {Form} from 'react-bootstrap';
import {useNavigate} from 'react-router';
import mark1 from '../assets/image/mark-1.png';
import mark2 from '../assets/image/mark-2.png';
import mark3 from '../assets/image/mark-3.png';
import chooseIcon from '../assets/image/choose.svg';
import notesIcon from '../assets/image/notes.svg';
import {getAccountData, choosePlan} from '../api/AccountApi';
import {checkoutStorageKey} from './MembershipCheckout';
import PageShell from '../components/PageShell';
import useIdempotencyKeys from '../hooks/useIdempotencyKeys';

export default function Membership() {
  const navigate = useNavigate();
  const keys = useIdempotencyKeys();
  const [isChecked, setIsChecked] = useState(false);
  const [accountData, setAccountData] = useState(null); // 儲存會員資料
  const [choosing, setChoosing] = useState(false);
  const levels = ['Silver', 'Gold', 'Platinum']; // 定義等級順序

  // 初始化時取得會員資料
  useEffect(() => {
    const fetchAccountData = async () => {
      try {
        const data = await getAccountData();
        setAccountData(data); // 儲存會員資料
      } catch (error) {
        console.error('Error fetching account data:', error);
      }
    };

    fetchAccountData();
  }, []);

  const handleCheckboxChange = (e) => {
    setIsChecked(e.target.checked);
  };

  const handleChoosePlan = async (plan) => {
    if (!isChecked) {
      alert('Please agree to the terms and conditions before choosing a plan.');
      return;
    }

    if (!accountData) {
      alert('Unable to retrieve account data. Please try again later.');
      return;
    }

    if (choosing) return;
    setChoosing(true);
    try {
      const memberId = accountData.id; // 假設會員編號是 `id` 欄位
      const responseData = await keys.run('membership-plan', {memberId, plan}, (idempotencyKey) =>
        choosePlan(memberId, plan, {idempotencyKey}),
      );
      if (!responseData.payment) {
        // Free plan: applied immediately.
        setChoosing(false);
        alert(`Plan "${plan}" is now active.`);
        return;
      }
      sessionStorage.setItem(checkoutStorageKey(responseData.id), JSON.stringify(responseData.payment));
      navigate(`/membership/checkout/${responseData.id}`);
    } catch (error) {
      console.error('Error choosing plan:', error);
      setChoosing(false);
      alert('An error occurred while choosing the plan. Please try again.');
    }
  };

  const isPlanDisabled = (plan) => {
    if (!accountData || plan == 'Silver') return true; // 如果尚未取得會員資料，禁用按鈕
    // 假設會員等級儲存在 `level` 欄位
    // console.log('Current Level:', accountData.customer_level);
    // console.log('Plan Level:', plan);
    const currentLevelIndex = levels.indexOf(accountData.customer_level);
    const planLevelIndex = levels.indexOf(plan);
    return planLevelIndex <= currentLevelIndex; // 禁用低於或等於目前等級的方案
  };

  return (
    <PageShell title="Membership Plans" showBackButton={true}>
      <h3
        style={{
          fontFamily: 'var(--font-inter)',
          color: 'var(--ink-muted)',
          fontWeight: 500,
          fontSize: '15px',
          marginBottom: '20px',
        }}
      >
        Choose your plan to start enjoying the benefits!
      </h3>
      <div className="membership-row">
        <div className="membership-col">
          <div className="membership-card">
            <img src={mark1} className="membership-mark" alt="Silver Plan" />
            <div className="membership-content">
              <div className="membership-cost">FREE</div>
              <div className="membership-bonus">
                Sign-up membership-bonus Point* <span>500</span>
              </div>
              <div className="membership-item">X-rays and Receipts Storage</div>
              <div className="membership-info">1GB</div>
              <div className="membership-item">Dental AI-assisted Diagnosis</div>
              <div className="membership-info">10 times / year</div>
              <div className="membership-item">Receipts and Hx Records Summary Reports</div>
              <div className="membership-info">1 report / year</div>
              <div className="membership-item">Treatment History Reports</div>
              <div className="membership-info">15 reports / year</div>
            </div>
            <button
              className="choose-btn"
              onClick={() => handleChoosePlan('Silver')}
              disabled={choosing || isPlanDisabled('Silver')} // 禁用條件
            >
              Choose Plan
              <img src={chooseIcon} alt="Choose" />
            </button>
          </div>
        </div>
        <div className="membership-col">
          <div className="membership-card">
            <img src={mark2} className="membership-mark" alt="Gold Plan" />
            <div className="membership-content">
              <div className="membership-cost">$29.99 / Year</div>
              <div className="membership-bonus">
                Sign-up membership-bonus Point* <span>3,000</span>
              </div>
              <div className="membership-item">X-rays and Receipts Storage</div>
              <div className="membership-info">5GB</div>
              <div className="membership-item">Dental AI-assisted Diagnosis</div>
              <div className="membership-info">30 times / year</div>
              <div className="membership-item">Receipts and Hx Records Summary Reports</div>
              <div className="membership-info">5 reports / year</div>
              <div className="membership-item">Treatment History Reports</div>
              <div className="membership-info">15 reports / year</div>
            </div>
            <button
              className="choose-btn"
              onClick={() => handleChoosePlan('Gold')}
              disabled={choosing || isPlanDisabled('Gold')} // 禁用條件
            >
              Choose Plan
              <img src={chooseIcon} alt="Choose" />
            </button>
          </div>
        </div>
        <div className="membership-col">
          <div className="membership-card">
            <img src={mark3} className="membership-mark" alt="Platinum Plan" />
            <div className="membership-content">
              <div className="membership-cost">$49.99 / Year</div>
              <div className="membership-bonus">
                Sign-up membership-bonus Point* <span>5,000</span>
              </div>
              <div className="membership-item">X-rays and Receipts Storage</div>
              <div className="membership-info">10GB</div>
              <div className="membership-item">Dental AI-assisted Diagnosis</div>
              <div className="membership-info">50 times / year</div>
              <div className="membership-item">Receipts and Hx Records Summary Reports</div>
              <div className="membership-info">10 reports / year</div>
              <div className="membership-item">Treatment History Reports</div>
              <div className="membership-info">15 reports / year</div>
            </div>
            <button
              className="choose-btn"
              onClick={() => handleChoosePlan('Platinum')}
              disabled={choosing || isPlanDisabled('Platinum')} // 禁用條件
            >
              Choose Plan
              <img src={chooseIcon} alt="Choose" />
            </button>
          </div>
        </div>
      </div>
      <div className="note-card">
        <div className="notes">
          <span>
            <img src={notesIcon} alt="Notes" />
            NOTES
          </span>
          <Form.Check>
            <Form.Check.Label>
              <Form.Check.Input type="checkbox" checked={isChecked} onChange={handleCheckboxChange} />I agree
              to the following:
            </Form.Check.Label>
          </Form.Check>
        </div>
        <ol>
          <li>The sign-up bonus points will expire at the end of the same year.</li>
          <li>
            The sign-up bonus points can be redeemed in the marketplace but are non-transferable and have no
            monetary equivalent.
          </li>
          <li>Both X-rays and receipts will be subject to the same storage limits.</li>
          <li>All membership sales are final ; cancellations and refunds are not permitted.</li>
        </ol>
      </div>
    </PageShell>
  );
}
